import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ExclamationCircleIcon,
  TrashIcon,
  ClockIcon,
  MapPinIcon,
  TagIcon,
} from "@heroicons/react/24/solid";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import { useSearchParams } from "react-router-dom";
import Dropdown from "../forms/Dropdown";
import { daysOfMonth, daysOfWeek } from "../../utils/calendar";
import Filters from "../layout/Filters";
import Card from "../layout/Card";
import DangerZone from "../forms/DangerZone";
import Indicator from "../layout/Indicator";
import {
  PointOfInterestState,
  createPointOfInterest,
  deletePointsOfInterest,
  getPointsOfInterest,
  updatePointOfInterest,
} from "../../slices/orientation";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";
import TimeSelector from "../forms/TimeSelector";
import Datepicker from "react-tailwindcss-datepicker";
import getDatepickerLocalisation from "../../utils/localisation";
import DisabledTextInput from "../forms/DisabledTextInput";

export default function PointsOfInterest() {
  const dispatch = useAppDispatch();

  const [searchParams] = useSearchParams();
  const searchCategory = searchParams.get("category");
  const searchType = searchParams.get("type");

  // Fetch data from server
  useEffect(() => {
    dispatch(
      getPointsOfInterest({ category: searchCategory, type: searchType }),
    );
  }, [dispatch, searchCategory, searchType]);

  // Set state
  const [category, setCategory] = useState({ value: "" });
  const [type, setType] = useState({ value: "" });
  const [selectedPoi, setSelectedPoi] = useState<{
    id: string;
    name: { [key: string]: string };
    category: string;
    description: { [key: string]: string };
    type: string;
    availability: string;
    availability_detail: {
      ALL: any;
      STD: any;
      STW: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
      STM: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
      SLD: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
    };
    status: string;
  }>({
    id: "",
    name: { EN: "", FR: "", IT: "", DE: "" },
    category: "ROO",
    description: { EN: "", FR: "", IT: "", DE: "" },
    type: "LOC",
    availability: "ALL",
    availability_detail: {
      ALL: null,
      STD: null,
      STW: [
        {
          id: 0,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ],
      STM: [
        {
          id: 0,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ],
      SLD: [],
    },
    status: "O",
  });
  const [createPoiForm, setCreatePoiForm] = useState<{
    name: { [key: string]: string };
    category: string;
    description: { [key: string]: string };
    type: string;
    availability: string;
    availability_detail: {
      ALL: any;
      STD: any;
      STW: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
      STM: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
      SLD: Array<{
        id: number;
        value: string;
        start_time: { hour: string; minute: string };
        end_time: { hour: string; minute: string };
      }>;
    };
    status: string;
  }>({
    name: { EN: "", FR: "", IT: "", DE: "" },
    category: "ROO",
    description: { EN: "", FR: "", IT: "", DE: "" },
    type: "LOC",
    availability: "ALL",
    availability_detail: {
      ALL: null,
      STD: null,
      STW: [
        {
          id: 0,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ],
      STM: [
        {
          id: 0,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ],
      SLD: [],
    },
    status: "O",
  });

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [recurringSldPlaceholder] = useState({
    startDate: null,
    endDate: null,
  });
  const [stdScheduleStartTime, setStdScheduleStartTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "", minute: "" });
  const [stdScheduleEndTime, setStdScheduleEndTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "", minute: "" });
  const [stdScheduleStartEditTime, setStdScheduleStartEditTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "", minute: "" });
  const [stdScheduleEndEditTime, setStdScheduleEndEditTime] = useState<{
    hour: any;
    minute: any;
  }>({ hour: "", minute: "" });

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { points_of_interest: pointsOfInterest, options } = useAppSelector(
    (state) => state.orientation,
  );
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectPoiToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [poiId] = args;
    const poiData = poisData.filter((poi) => poi.id === poiId)[0];
    let availabilityDetail: any = {
      ALL: null,
      STD: null,
      STW: [
        {
          id: 0,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ],
      STM: [
        {
          id: 0,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ],
      SLD: [],
    };
    if (poiData.availability[0] === "ALL") {
      availabilityDetail.ALL = true;
    } else if (poiData.availability[0] === "STD") {
      availabilityDetail.STD = {
        start_time: {
          hour:
            poiData.availability_detail.STD === null
              ? "0"
              : poiData.availability_detail.STD.start_time.hour.toString(),
          minute:
            poiData.availability_detail.STD === null
              ? "0"
              : poiData.availability_detail.STD.start_time.minute.toString(),
        },
        end_time: {
          hour:
            poiData.availability_detail.STD === null
              ? "0"
              : poiData.availability_detail.STD.end_time.hour.toString(),
          minute:
            poiData.availability_detail.STD === null
              ? "0"
              : poiData.availability_detail.STD.end_time.minute.toString(),
        },
      };
      setStdScheduleStartEditTime(availabilityDetail.STD.start_time);
      setStdScheduleEndEditTime(availabilityDetail.STD.end_time);
    } else if (poiData.availability[0] === "STW") {
      availabilityDetail.STW =
        poiData.availability_detail.STW === null
          ? [
              {
                id: 0,
                value: "1",
                start_time: { hour: "0", minute: "0" },
                end_time: { hour: "0", minute: "0" },
              },
            ]
          : poiData.availability_detail.STW.map((x: any) => ({
              id: x.id,
              value: x.day_of_week,
              start_time: {
                hour: x.start_time.hour.toString(),
                minute: x.start_time.minute.toString(),
              },
              end_time: {
                hour: x.end_time.hour.toString(),
                minute: x.end_time.minute.toString(),
              },
            }));
    } else if (poiData.availability[0] === "STM") {
      availabilityDetail.STM =
        poiData.availability_detail.STM === null
          ? [
              {
                id: 0,
                value: "1",
                start_time: { hour: "0", minute: "0" },
                end_time: { hour: "0", minute: "0" },
              },
            ]
          : poiData.availability_detail.STM.map((x: any) => ({
              id: x.id,
              value: x.day_of_month,
              start_time: {
                hour: x.start_time.hour.toString(),
                minute: x.start_time.minute.toString(),
              },
              end_time: {
                hour: x.end_time.hour.toString(),
                minute: x.end_time.minute.toString(),
              },
            }));
    } else if (poiData.availability[0] === "SLD") {
      availabilityDetail.SLD =
        poiData.availability_detail.SLD === null
          ? []
          : poiData.availability_detail.SLD.map((x: any) => ({
              id: x.id,
              value: x.date,
              start_time: {
                hour: x.start_time.hour.toString(),
                minute: x.start_time.minute.toString(),
              },
              end_time: {
                hour: x.end_time.hour.toString(),
                minute: x.end_time.minute.toString(),
              },
            }));
    }
    setSelectedPoi({
      id: poiData.id,
      name: poiData.name,
      category: poiData.category[0],
      description: poiData.description,
      type: poiData.type[0],
      availability: poiData.availability[0],
      availability_detail: availabilityDetail,
      status: poiData.status[0],
    });
    setShowEditModal(true);
  }

  function openCreatePoiModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  async function confirmCreatePoi(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      name,
      category,
      description,
      type,
      availability,
      availability_detail: availabilityDetail,
      status,
    } = createPoiForm;

    // Validate text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (category === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_category", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    if (type === "") {
      dispatch(
        addAlert(generateAlertText("missing_type", language), "danger", 5000),
      );
      return;
    }
    if (availability === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_availability", language),
          "danger",
          5000,
        ),
      );
      return;
    }

    // Validate availability
    let availabilityObj: any = {
      ALL: false,
      STD: null,
      STW: null,
      STM: null,
      SLD: null,
    };
    if (availability === "ALL") {
      availabilityObj.ALL = true;
    } else if (availability === "STD") {
      const { hour: startHour, minute: startMinute } = stdScheduleStartTime;
      const { hour: endHour, minute: endMinute } = stdScheduleEndTime;
      if (startHour === "" || startMinute === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_start_datetime", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      if (endHour === "" || endMinute === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_end_datetime", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      availabilityObj.STD = {
        start_time: {
          hour: parseInt(startHour, 10),
          minute: parseInt(startMinute, 10),
        },
        end_time: {
          hour: parseInt(endHour, 10),
          minute: parseInt(endMinute, 10),
        },
      };
    } else if (availability === "STW") {
      const detailValues = availabilityDetail.STW;
      for (let i = 0; i < detailValues.length; i++) {
        const {
          value,
          start_time: startTime,
          end_time: endTime,
        } = detailValues[i];
        if (
          value === "" ||
          startTime.hour === "" ||
          startTime.minute === "" ||
          endTime.hour === "" ||
          endTime.minute === ""
        ) {
          dispatch(
            addAlert(
              generateAlertText("missing_recurring_schedule_detail", language),
              "danger",
              5000,
            ),
          );
          return;
        }
      }
      availabilityObj.STW = detailValues.map((x: any, idx: number) => ({
        id: idx,
        day_of_week: x.value,
        start_time: {
          hour: parseInt(x.start_time.hour, 10),
          minute: parseInt(x.start_time.minute, 10),
        },
        end_time: {
          hour: parseInt(x.end_time.hour, 10),
          minute: parseInt(x.end_time.minute, 10),
        },
      }));
    } else if (availability === "STM") {
      const detailValues = availabilityDetail.STM;
      for (let i = 0; i < detailValues.length; i++) {
        const {
          value,
          start_time: startTime,
          end_time: endTime,
        } = detailValues[i];
        if (
          value === "" ||
          startTime.hour === "" ||
          startTime.minute === "" ||
          endTime.hour === "" ||
          endTime.minute === ""
        ) {
          dispatch(
            addAlert(
              generateAlertText("missing_recurring_schedule_detail", language),
              "danger",
              5000,
            ),
          );
          return;
        }
      }
      availabilityObj.STM = detailValues.map((x: any, idx: number) => ({
        id: idx,
        day_of_month: x.value,
        start_time: {
          hour: parseInt(x.start_time.hour, 10),
          minute: parseInt(x.start_time.minute, 10),
        },
        end_time: {
          hour: parseInt(x.end_time.hour, 10),
          minute: parseInt(x.end_time.minute, 10),
        },
      }));
    } else if (availability === "SLD") {
      const detailValues = availabilityDetail.SLD;
      if (detailValues.length === 0) {
        dispatch(
          addAlert(
            generateAlertText("missing_selected_days", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      for (let i = 0; i < detailValues.length; i++) {
        const {
          value,
          start_time: startTime,
          end_time: endTime,
        } = detailValues[i];
        if (
          value === "" ||
          startTime.hour === "" ||
          startTime.minute === "" ||
          endTime.hour === "" ||
          endTime.minute === ""
        ) {
          dispatch(
            addAlert(
              generateAlertText("missing_recurring_schedule_detail", language),
              "danger",
              5000,
            ),
          );
          return;
        }
      }
      availabilityObj.SLD = detailValues.map((x: any, idx: number) => ({
        id: idx,
        date: x.value,
        start_time: {
          hour: parseInt(x.start_time.hour, 10),
          minute: parseInt(x.start_time.minute, 10),
        },
        end_time: {
          hour: parseInt(x.end_time.hour, 10),
          minute: parseInt(x.end_time.minute, 10),
        },
      }));
    }

    setShowCreateModal(false);
    const res: any = await dispatch(
      createPointOfInterest({
        name,
        property: user.property.id,
        category,
        description,
        type,
        availability,
        availabilityDetail: availabilityObj,
        status,
      }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(generateAlertText("create_poi", language), "success", 3000),
      );
      setCreatePoiForm({
        name: { EN: "", FR: "", IT: "", DE: "" },
        category: "ROO",
        description: { EN: "", FR: "", IT: "", DE: "" },
        type: "OTH",
        availability: "ALL",
        availability_detail: {
          ALL: null,
          STD: null,
          STW: [
            {
              id: 0,
              value: "1",
              start_time: { hour: "0", minute: "0" },
              end_time: { hour: "0", minute: "0" },
            },
          ],
          STM: [
            {
              id: 0,
              value: "1",
              start_time: { hour: "0", minute: "0" },
              end_time: { hour: "0", minute: "0" },
            },
          ],
          SLD: [],
        },
        status: "O",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditPoi(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const {
      id,
      name,
      category,
      description,
      type,
      availability,
      availability_detail: availabilityDetail,
      status,
    } = selectedPoi;

    // Validate text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }
    if (category === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_category", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    if (type === "") {
      dispatch(
        addAlert(generateAlertText("missing_type", language), "danger", 5000),
      );
      return;
    }
    if (availability === "") {
      dispatch(
        addAlert(
          generateAlertText("missing_availability", language),
          "danger",
          5000,
        ),
      );
      return;
    }

    // Validate availability
    let availabilityObj: any = {
      ALL: false,
      STD: null,
      STW: null,
      STM: null,
      SLD: null,
    };
    if (availability === "ALL") {
      availabilityObj.ALL = true;
    } else if (availability === "STD") {
      const { hour: startHour, minute: startMinute } = stdScheduleStartEditTime;
      const { hour: endHour, minute: endMinute } = stdScheduleEndEditTime;
      if (startHour === "" || startMinute === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_start_datetime", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      if (endHour === "" || endMinute === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_end_datetime", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      availabilityObj.STD = {
        start_time: {
          hour: parseInt(startHour, 10),
          minute: parseInt(startMinute, 10),
        },
        end_time: {
          hour: parseInt(endHour, 10),
          minute: parseInt(endMinute, 10),
        },
      };
    } else if (availability === "STW") {
      const detailValues = availabilityDetail.STW;
      for (let i = 0; i < detailValues.length; i++) {
        const {
          value,
          start_time: startTime,
          end_time: endTime,
        } = detailValues[i];
        if (
          value === "" ||
          startTime.hour === "" ||
          startTime.minute === "" ||
          endTime.hour === "" ||
          endTime.minute === ""
        ) {
          dispatch(
            addAlert(
              generateAlertText("missing_recurring_schedule_detail", language),
              "danger",
              5000,
            ),
          );
          return;
        }
      }
      availabilityObj.STW = detailValues.map((x: any, idx: number) => ({
        id: idx,
        day_of_week: x.value,
        start_time: {
          hour: parseInt(x.start_time.hour, 10),
          minute: parseInt(x.start_time.minute, 10),
        },
        end_time: {
          hour: parseInt(x.end_time.hour, 10),
          minute: parseInt(x.end_time.minute, 10),
        },
      }));
    } else if (availability === "STM") {
      const detailValues = availabilityDetail.STM;
      for (let i = 0; i < detailValues.length; i++) {
        const {
          value,
          start_time: startTime,
          end_time: endTime,
        } = detailValues[i];
        if (
          value === "" ||
          startTime.hour === "" ||
          startTime.minute === "" ||
          endTime.hour === "" ||
          endTime.minute === ""
        ) {
          dispatch(
            addAlert(
              generateAlertText("missing_recurring_schedule_detail", language),
              "danger",
              5000,
            ),
          );
          return;
        }
      }
      availabilityObj.STM = detailValues.map((x: any, idx: number) => ({
        id: idx,
        day_of_month: x.value,
        start_time: {
          hour: parseInt(x.start_time.hour, 10),
          minute: parseInt(x.start_time.minute, 10),
        },
        end_time: {
          hour: parseInt(x.end_time.hour, 10),
          minute: parseInt(x.end_time.minute, 10),
        },
      }));
    } else if (availability === "SLD") {
      const detailValues = availabilityDetail.SLD;
      if (detailValues.length === 0) {
        dispatch(
          addAlert(
            generateAlertText("missing_selected_days", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      for (let i = 0; i < detailValues.length; i++) {
        const {
          value,
          start_time: startTime,
          end_time: endTime,
        } = detailValues[i];
        if (
          value === "" ||
          startTime.hour === "" ||
          startTime.minute === "" ||
          endTime.hour === "" ||
          endTime.minute === ""
        ) {
          dispatch(
            addAlert(
              generateAlertText("missing_recurring_schedule_detail", language),
              "danger",
              5000,
            ),
          );
          return;
        }
      }
      availabilityObj.SLD = detailValues.map((x: any, idx: number) => ({
        id: idx,
        date: x.value,
        start_time: {
          hour: parseInt(x.start_time.hour, 10),
          minute: parseInt(x.start_time.minute, 10),
        },
        end_time: {
          hour: parseInt(x.end_time.hour, 10),
          minute: parseInt(x.end_time.minute, 10),
        },
      }));
    }

    setShowEditModal(false);
    const res: any = await dispatch(
      updatePointOfInterest({
        poiId: id,
        name,
        property: user.property.id,
        category,
        description,
        type,
        availability,
        availabilityDetail: availabilityObj,
        status,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(generateAlertText("update_poi", language), "success", 3000),
      );
      setSelectedPoi({
        id: "",
        name: { EN: "", FR: "", IT: "", DE: "" },
        category: "ROO",
        description: { EN: "", FR: "", IT: "", DE: "" },
        type: "OTH",
        availability: "ALL",
        availability_detail: {
          ALL: null,
          STD: null,
          STW: [
            {
              id: 0,
              value: "1",
              start_time: { hour: "0", minute: "0" },
              end_time: { hour: "0", minute: "0" },
            },
          ],
          STM: [
            {
              id: 0,
              value: "1",
              start_time: { hour: "0", minute: "0" },
              end_time: { hour: "0", minute: "0" },
            },
          ],
          SLD: [],
        },
        status: "O",
      });
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function confirmDeletePoi(e: any, args: any[]) {
    e.preventDefault();
    const [poiIds] = args;
    if (poiIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (poiIds === null || poiIds === undefined) {
      dispatch(
        addAlert(generateAlertText("missing_pois", language), "danger", 3000),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(
      deletePointsOfInterest({ poiIds: [poiIds] }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function increaseRecurringDetailValueCount(e: any, args: any[]) {
    e.preventDefault();
    const [type] = args;
    if (type === "STW") {
      const existingValues = [...createPoiForm.availability_detail.STW];
      const newId = existingValues.length;
      const newValues = [
        ...existingValues,
        {
          id: newId,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ];
      setCreatePoiForm({
        ...createPoiForm,
        availability_detail: {
          ...createPoiForm.availability_detail,
          STW: newValues,
        },
      });
    } else if (type === "STM") {
      const existingValues = [...createPoiForm.availability_detail.STM];
      const newId = existingValues.length;
      existingValues.push({
        id: newId,
        value: "1",
        start_time: { hour: "0", minute: "0" },
        end_time: { hour: "0", minute: "0" },
      });
      setCreatePoiForm({
        ...createPoiForm,
        availability_detail: {
          ...createPoiForm.availability_detail,
          STM: existingValues,
        },
      });
    }
  }

  function increaseRecurringDetailValueEditCount(e: any, args: any[]) {
    e.preventDefault();
    const [type] = args;
    if (type === "STW") {
      const existingValues = [...selectedPoi.availability_detail.STW];
      const newId = existingValues.length;
      const newValues = [
        ...existingValues,
        {
          id: newId,
          value: "1",
          start_time: { hour: "0", minute: "0" },
          end_time: { hour: "0", minute: "0" },
        },
      ];
      setSelectedPoi({
        ...selectedPoi,
        availability_detail: {
          ...selectedPoi.availability_detail,
          STW: newValues,
        },
      });
    } else if (type === "STM") {
      const existingValues = [...selectedPoi.availability_detail.STM];
      const newId = existingValues.length;
      existingValues.push({
        id: newId,
        value: "1",
        start_time: { hour: "0", minute: "0" },
        end_time: { hour: "0", minute: "0" },
      });
      setSelectedPoi({
        ...selectedPoi,
        availability_detail: {
          ...selectedPoi.availability_detail,
          STM: existingValues,
        },
      });
    }
  }

  function addDateToRecurringSld(value: any) {
    const existingValues = [...createPoiForm.availability_detail.SLD];
    const newId = existingValues.length;
    const newValues = [
      ...existingValues,
      {
        id: newId,
        value: value.startDate,
        start_time: { hour: "0", minute: "0" },
        end_time: { hour: "0", minute: "0" },
      },
    ];
    setCreatePoiForm({
      ...createPoiForm,
      availability_detail: {
        ...createPoiForm.availability_detail,
        SLD: newValues,
      },
    });
  }

  function addDateToRecurringEditSld(value: any) {
    const existingValues = [...selectedPoi.availability_detail.SLD];
    const newId = existingValues.length;
    const newValues = [
      ...existingValues,
      {
        id: newId,
        value: value.startDate,
        start_time: { hour: "0", minute: "0" },
        end_time: { hour: "0", minute: "0" },
      },
    ];
    setSelectedPoi({
      ...selectedPoi,
      availability_detail: {
        ...selectedPoi.availability_detail,
        SLD: newValues,
      },
    });
  }

  // Format data and filter by query param tag
  const poisData = pointsOfInterest.data === null ? [] : pointsOfInterest.data;
  const poiTypes =
    options.type === undefined
      ? []
      : Object.keys(options.type).map((key) => ({
          value: key,
          label: options.type[key][language],
        }));
  const poiTypesChoices =
    options.type === undefined
      ? []
      : Object.keys(options.type).map((key) => [
          key,
          options.type[key][language],
        ]);
  const poiCategory =
    options.category === undefined
      ? []
      : Object.keys(options.category).map((key) => ({
          value: key,
          label: options.category[key][language],
        }));
  const poiCategoryChoices =
    options.category === undefined
      ? []
      : Object.keys(options.category).map((key) => [
          key,
          options.category[key][language],
        ]);
  const poiAvailabilityChoices =
    options.availability === undefined
      ? []
      : Object.keys(options.availability).map((key) => [
          key,
          options.availability[key][language],
        ]);
  const poiStatusChoices =
    options.status === undefined
      ? []
      : Object.keys(options.status).map((key) => [
          key,
          options.status[key][language],
        ]);
  const daysOfTheWeek = daysOfWeek(language);
  const daysOfTheMonth = daysOfMonth();
  const hours = Array.from({ length: 24 }, (x, i) => i);
  const minutes = Array.from({ length: 60 }, (x, i) => i);

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("pois_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreatePoiModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_poi", language)}
              </Button>
            }
          />
        }
      />

      {/* Create point of interest modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_poi", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreatePoi}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_poi", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createPoiForm}
                stateName="name"
                onChange={setCreatePoiForm}
                defaultLanguage={language}
                languageChoices={
                  poisData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(poisData[0].name)
                }
              />

              {/* Category */}
              <Dropdown
                id="category"
                className="col-span-full"
                label={translateText("category", language)}
                showLabel={true}
                required={true}
                state={createPoiForm}
                stateName="category"
                onChange={setCreatePoiForm}
                defaultValue="ROO"
              >
                {poiCategoryChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Description */}
              <TextInputMultiLanguage
                id="description"
                type="text"
                label={translateText("description", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createPoiForm}
                stateName="description"
                onChange={setCreatePoiForm}
                defaultLanguage={language}
                languageChoices={
                  poisData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(poisData[0].name)
                }
              />

              {/* Type */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("type", language)}
                showLabel={true}
                required={true}
                state={createPoiForm}
                stateName="type"
                onChange={setCreatePoiForm}
                defaultValue="LOC"
              >
                {poiTypesChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Availability */}
              <Dropdown
                id="availability"
                className="col-span-full"
                label={translateText("availability", language)}
                showLabel={true}
                required={true}
                state={createPoiForm}
                stateName="availability"
                onChange={setCreatePoiForm}
                defaultValue="ALL"
              >
                {poiAvailabilityChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* If availability is ALL */}
              {createPoiForm.availability === "STD" && (
                <>
                  {/* Start time */}
                  <TimeSelector
                    label={translateText("start_time", language)}
                    showLabel={true}
                    timeState={stdScheduleStartTime}
                    timeHandler={setStdScheduleStartTime}
                    language={language}
                  />
                  {/* End time */}
                  <TimeSelector
                    label={translateText("end_time", language)}
                    showLabel={true}
                    timeState={stdScheduleEndTime}
                    timeHandler={setStdScheduleEndTime}
                    language={language}
                  />
                </>
              )}

              {/* If availability is STW */}
              {createPoiForm.availability === "STW" && (
                <>
                  {createPoiForm.availability_detail.STW.map((value, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                    >
                      {/* Day of week */}
                      <div>
                        {/* Delete button if necessary */}
                        {createPoiForm.availability_detail.STW.length > 1 && (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className={`${getButtonStyle(
                                "solid",
                                "danger",
                              )} mb-3`}
                              onClick={(e) => {
                                e.preventDefault();
                                const existingValues =
                                  createPoiForm.availability_detail.STW;
                                existingValues.splice(index, 1);
                                const newValues = existingValues.map(
                                  (value, index) => ({ ...value, id: index }),
                                );
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    STW: newValues,
                                  },
                                });
                              }}
                            >
                              <TrashIcon
                                className={`h-5 w-5 text-white`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        )}

                        <div className="col-span-full">
                          <select
                            id={`dayofweek-${index}-recurring`}
                            className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                            required={true}
                            onChange={(e) => {
                              const existingValues = [
                                ...createPoiForm.availability_detail.STW,
                              ];
                              existingValues[index].value = e.target.value;
                              setCreatePoiForm({
                                ...createPoiForm,
                                availability_detail: {
                                  ...createPoiForm.availability_detail,
                                  STW: existingValues,
                                },
                              });
                            }}
                            value={parseInt(
                              createPoiForm.availability_detail.STW[index]
                                .value,
                              10,
                            )}
                          >
                            {daysOfTheWeek.map((opt) => (
                              <option key={opt[0]} value={opt[0]}>
                                {opt[1]}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Start and end time */}
                      <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                        {/* Start time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("start_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`dayofweek-start-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.STW,
                                ];
                                existingValues[index].start_time.hour =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    STW: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`dayofweek-start-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.STW,
                                ];
                                existingValues[index].start_time.minute =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    STW: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* End time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("end_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`dayofweek-end-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.STW,
                                ];
                                existingValues[index].end_time.hour =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    STW: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`dayofweek-end-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.STW,
                                ];
                                existingValues[index].end_time.minute =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    STW: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    className={`${getButtonStyle("solid", "primary")}`}
                    onClick={increaseRecurringDetailValueCount}
                    onClickArgs={["STW"]}
                  >
                    <PlusCircleIcon
                      className={`h-5 w-5 text-white mr-1`}
                      aria-hidden="true"
                    />
                    {translateText("add_another_time", language)}
                  </Button>
                </>
              )}

              {/* If recurring schedule is STM */}
              {createPoiForm.availability === "STM" && (
                <>
                  {createPoiForm.availability_detail.STM.map((value, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                    >
                      {/* Day of month */}
                      <div>
                        {/* Delete button if necessary */}
                        {createPoiForm.availability_detail.STM.length > 1 && (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className={`${getButtonStyle(
                                "solid",
                                "danger",
                              )} mb-3`}
                              onClick={(e) => {
                                e.preventDefault();
                                const existingValues =
                                  createPoiForm.availability_detail.STM;
                                existingValues.splice(index, 1);
                                const newValues = existingValues.map(
                                  (value, index) => ({ ...value, id: index }),
                                );
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    STM: newValues,
                                  },
                                });
                              }}
                            >
                              <TrashIcon
                                className={`h-5 w-5 text-white`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        )}

                        <div className="col-span-full">
                          <select
                            id={`dayofmonth-${index}-recurring`}
                            className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                            required={true}
                            onChange={(e) => {
                              const existingValues = [
                                ...createPoiForm.availability_detail.STM,
                              ];
                              existingValues[index].value = e.target.value;
                              setCreatePoiForm({
                                ...createPoiForm,
                                availability_detail: {
                                  ...createPoiForm.availability_detail,
                                  STM: existingValues,
                                },
                              });
                            }}
                            value={parseInt(
                              createPoiForm.availability_detail.STM[index]
                                .value,
                              10,
                            )}
                          >
                            {daysOfTheMonth.map((opt) => (
                              <option key={opt[0]} value={opt[0]}>
                                {opt[1]}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Start and end time */}
                      <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                        {/* Start time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("start_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`dayofmonth-start-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.STM,
                                ];
                                existingValues[index].start_time.hour =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    STM: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`dayofmonth-start-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.STM,
                                ];
                                existingValues[index].start_time.minute =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    STM: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* End time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("end_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`dayofmonth-end-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.STM,
                                ];
                                existingValues[index].end_time.hour =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    STM: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`dayofweek-end-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.STM,
                                ];
                                existingValues[index].end_time.minute =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    STM: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    className={`${getButtonStyle("solid", "primary")}`}
                    onClick={increaseRecurringDetailValueCount}
                    onClickArgs={["STM"]}
                  >
                    <PlusCircleIcon
                      className={`h-5 w-5 text-white mr-1`}
                      aria-hidden="true"
                    />
                    {translateText("add_another_time", language)}
                  </Button>
                </>
              )}

              {/* If recurring schedule is SLD */}
              {createPoiForm.availability === "SLD" && (
                <>
                  <Datepicker
                    i18n={getDatepickerLocalisation(language)}
                    asSingle={true}
                    startFrom={new Date()}
                    minDate={new Date()}
                    value={recurringSldPlaceholder}
                    placeholder={translateText("select_dates", language)}
                    displayFormat={""}
                    onChange={addDateToRecurringSld}
                  />
                  {createPoiForm.availability_detail.SLD.map((value, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                    >
                      {/* Date */}
                      <div>
                        {/* Delete button if necessary */}
                        {createPoiForm.availability_detail.SLD.length > 1 && (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className={`${getButtonStyle(
                                "solid",
                                "danger",
                              )} mb-3`}
                              onClick={(e) => {
                                e.preventDefault();
                                const existingValues =
                                  createPoiForm.availability_detail.SLD;
                                existingValues.splice(index, 1);
                                const newValues = existingValues.map(
                                  (value, index) => ({ ...value, id: index }),
                                );
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    SLD: newValues,
                                  },
                                });
                              }}
                            >
                              <TrashIcon
                                className={`h-5 w-5 text-white`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        )}
                        <DisabledTextInput
                          id={`sld-date-${index}-recurring`}
                          label=""
                          className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                          showLabel={false}
                          value={value.value}
                        />
                      </div>

                      {/* Start and end time */}
                      <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                        {/* Start time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("start_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`sld-start-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.SLD,
                                ];
                                existingValues[index].start_time.hour =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    SLD: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`sld-start-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.SLD,
                                ];
                                existingValues[index].start_time.minute =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    SLD: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* End time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("end_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`sld-end-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.SLD,
                                ];
                                existingValues[index].end_time.hour =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    SLD: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`sld-end-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...createPoiForm.availability_detail.SLD,
                                ];
                                existingValues[index].end_time.minute =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...createPoiForm,
                                  availability_detail: {
                                    ...createPoiForm.availability_detail,
                                    SLD: existingValues,
                                  },
                                });
                              }}
                              defaultValue=""
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Status */}
              <Dropdown
                id="status"
                className="col-span-full"
                label={translateText("status", language)}
                showLabel={true}
                required={true}
                state={createPoiForm}
                stateName="status"
                onChange={setCreatePoiForm}
                defaultValue="O"
              >
                {poiStatusChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit point of interest modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_poi", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditPoi}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_poi", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedPoi}
                stateName="name"
                onChange={setSelectedPoi}
                defaultLanguage={language}
                languageChoices={
                  poisData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(poisData[0].name)
                }
              />

              {/* Category */}
              <Dropdown
                id="category"
                className="col-span-full"
                label={translateText("category", language)}
                showLabel={true}
                required={true}
                state={selectedPoi}
                stateName="category"
                onChange={setSelectedPoi}
                defaultValue={selectedPoi.category}
              >
                {poiCategoryChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Description */}
              <TextInputMultiLanguage
                id="description"
                type="text"
                label={translateText("description", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedPoi}
                stateName="description"
                onChange={setSelectedPoi}
                defaultLanguage={language}
                languageChoices={
                  poisData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(poisData[0].name)
                }
              />

              {/* Type */}
              <Dropdown
                id="type"
                className="col-span-full"
                label={translateText("type", language)}
                showLabel={true}
                required={true}
                state={selectedPoi}
                stateName="type"
                onChange={setSelectedPoi}
                defaultValue={selectedPoi.type}
              >
                {poiTypesChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Availability */}
              <Dropdown
                id="availability"
                className="col-span-full"
                label={translateText("availability", language)}
                showLabel={true}
                required={true}
                state={selectedPoi}
                stateName="availability"
                onChange={setSelectedPoi}
                defaultValue={selectedPoi.availability}
              >
                {poiAvailabilityChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* If availability is ALL */}
              {selectedPoi.availability === "STD" && (
                <>
                  {/* Start time */}
                  <TimeSelector
                    label={translateText("start_time", language)}
                    showLabel={true}
                    timeState={stdScheduleStartEditTime}
                    timeHandler={setStdScheduleStartEditTime}
                    language={language}
                  />
                  {/* End time */}
                  <TimeSelector
                    label={translateText("end_time", language)}
                    showLabel={true}
                    timeState={stdScheduleEndEditTime}
                    timeHandler={setStdScheduleEndEditTime}
                    language={language}
                  />
                </>
              )}

              {/* If availability is STW */}
              {selectedPoi.availability === "STW" && (
                <>
                  {selectedPoi.availability_detail.STW.map((value, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                    >
                      {/* Day of week */}
                      <div>
                        {/* Delete button if necessary */}
                        {selectedPoi.availability_detail.STW.length > 1 && (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className={`${getButtonStyle(
                                "solid",
                                "danger",
                              )} mb-3`}
                              onClick={(e) => {
                                e.preventDefault();
                                const existingValues =
                                  selectedPoi.availability_detail.STW;
                                existingValues.splice(index, 1);
                                const newValues = existingValues.map(
                                  (value, index) => ({ ...value, id: index }),
                                );
                                setSelectedPoi({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    STW: newValues,
                                  },
                                });
                              }}
                            >
                              <TrashIcon
                                className={`h-5 w-5 text-white`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        )}

                        <div className="col-span-full">
                          <select
                            id={`dayofweek-${index}-recurring`}
                            className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                            required={true}
                            onChange={(e) => {
                              const existingValues = [
                                ...selectedPoi.availability_detail.STW,
                              ];
                              existingValues[index].value = e.target.value;
                              setSelectedPoi({
                                ...selectedPoi,
                                availability_detail: {
                                  ...selectedPoi.availability_detail,
                                  STW: existingValues,
                                },
                              });
                            }}
                            value={parseInt(
                              selectedPoi.availability_detail.STW[index].value,
                              10,
                            )}
                          >
                            {daysOfTheWeek.map((opt) => (
                              <option key={opt[0]} value={opt[0]}>
                                {opt[1]}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Start and end time */}
                      <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                        {/* Start time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("start_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`dayofweek-start-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.STW,
                                ];
                                existingValues[index].start_time.hour =
                                  e.target.value;
                                setSelectedPoi({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    STW: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.STW[index]
                                  .start_time.hour,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`dayofweek-start-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.STW,
                                ];
                                existingValues[index].start_time.minute =
                                  e.target.value;
                                setSelectedPoi({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    STW: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.STW[index]
                                  .start_time.minute,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* End time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("end_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`dayofweek-end-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.STW,
                                ];
                                existingValues[index].end_time.hour =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    STW: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.STW[index]
                                  .end_time.hour,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`dayofweek-end-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.STW,
                                ];
                                existingValues[index].end_time.minute =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    STW: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.STW[index]
                                  .end_time.minute,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    className={`${getButtonStyle("solid", "primary")}`}
                    onClick={increaseRecurringDetailValueEditCount}
                    onClickArgs={["STW"]}
                  >
                    <PlusCircleIcon
                      className={`h-5 w-5 text-white mr-1`}
                      aria-hidden="true"
                    />
                    {translateText("add_another_time", language)}
                  </Button>
                </>
              )}

              {/* If recurring schedule is STM */}
              {selectedPoi.availability === "STM" && (
                <>
                  {selectedPoi.availability_detail.STM.map((value, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                    >
                      {/* Day of month */}
                      <div>
                        {/* Delete button if necessary */}
                        {selectedPoi.availability_detail.STM.length > 1 && (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className={`${getButtonStyle(
                                "solid",
                                "danger",
                              )} mb-3`}
                              onClick={(e) => {
                                e.preventDefault();
                                const existingValues =
                                  selectedPoi.availability_detail.STM;
                                existingValues.splice(index, 1);
                                const newValues = existingValues.map(
                                  (value, index) => ({ ...value, id: index }),
                                );
                                setCreatePoiForm({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    STM: newValues,
                                  },
                                });
                              }}
                            >
                              <TrashIcon
                                className={`h-5 w-5 text-white`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        )}

                        <div className="col-span-full">
                          <select
                            id={`dayofmonth-${index}-recurring`}
                            className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                            required={true}
                            onChange={(e) => {
                              const existingValues = [
                                ...selectedPoi.availability_detail.STM,
                              ];
                              existingValues[index].value = e.target.value;
                              setCreatePoiForm({
                                ...selectedPoi,
                                availability_detail: {
                                  ...selectedPoi.availability_detail,
                                  STM: existingValues,
                                },
                              });
                            }}
                            value={parseInt(
                              selectedPoi.availability_detail.STM[index].value,
                              10,
                            )}
                          >
                            {daysOfTheMonth.map((opt) => (
                              <option key={opt[0]} value={opt[0]}>
                                {opt[1]}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Start and end time */}
                      <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                        {/* Start time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("start_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`dayofmonth-start-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.STM,
                                ];
                                existingValues[index].start_time.hour =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    STM: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.STM[index]
                                  .start_time.hour,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`dayofmonth-start-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.STM,
                                ];
                                existingValues[index].start_time.minute =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    STM: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.STM[index]
                                  .start_time.minute,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* End time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("end_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`dayofmonth-end-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.STM,
                                ];
                                existingValues[index].end_time.hour =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    STM: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.STM[index]
                                  .end_time.hour,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`dayofweek-end-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.STM,
                                ];
                                existingValues[index].end_time.minute =
                                  e.target.value;
                                setCreatePoiForm({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    STM: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.STM[index]
                                  .end_time.minute,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    className={`${getButtonStyle("solid", "primary")}`}
                    onClick={increaseRecurringDetailValueEditCount}
                    onClickArgs={["STM"]}
                  >
                    <PlusCircleIcon
                      className={`h-5 w-5 text-white mr-1`}
                      aria-hidden="true"
                    />
                    {translateText("add_another_time", language)}
                  </Button>
                </>
              )}

              {/* If recurring schedule is SLD */}
              {selectedPoi.availability === "SLD" && (
                <>
                  <Datepicker
                    i18n={getDatepickerLocalisation(language)}
                    asSingle={true}
                    startFrom={new Date()}
                    minDate={new Date()}
                    value={recurringSldPlaceholder}
                    placeholder={translateText("select_dates", language)}
                    displayFormat={""}
                    onChange={addDateToRecurringEditSld}
                  />
                  {selectedPoi.availability_detail.SLD.map((value, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                    >
                      {/* Date */}
                      <div>
                        {/* Delete button if necessary */}
                        {selectedPoi.availability_detail.SLD.length > 1 && (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className={`${getButtonStyle(
                                "solid",
                                "danger",
                              )} mb-3`}
                              onClick={(e) => {
                                e.preventDefault();
                                const existingValues =
                                  selectedPoi.availability_detail.SLD;
                                existingValues.splice(index, 1);
                                const newValues = existingValues.map(
                                  (value, index) => ({ ...value, id: index }),
                                );
                                setSelectedPoi({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    SLD: newValues,
                                  },
                                });
                              }}
                            >
                              <TrashIcon
                                className={`h-5 w-5 text-white`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        )}
                        <DisabledTextInput
                          id={`sld-date-${index}-recurring`}
                          label=""
                          className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                          showLabel={false}
                          value={value.value}
                        />
                      </div>

                      {/* Start and end time */}
                      <div className="grid md:grid-cols-2 grid-cols-1 gap-x-2 gap-y-6">
                        {/* Start time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("start_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`sld-start-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.SLD,
                                ];
                                existingValues[index].start_time.hour =
                                  e.target.value;
                                setSelectedPoi({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    SLD: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.SLD[index]
                                  .start_time.hour,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`sld-start-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.SLD,
                                ];
                                existingValues[index].start_time.minute =
                                  e.target.value;
                                setSelectedPoi({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    SLD: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.SLD[index]
                                  .start_time.minute,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* End time */}
                        <div>
                          <label
                            className={`mb-3 block text-left text-sm font-medium text-black`}
                          >
                            {translateText("end_time", language)}
                          </label>
                          <div className="grid grid-cols-2 gap-x-2">
                            <select
                              id={`sld-end-time-hour-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.SLD,
                                ];
                                existingValues[index].end_time.hour =
                                  e.target.value;
                                setSelectedPoi({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    SLD: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.SLD[index]
                                  .end_time.hour,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("hour", language)}
                              </option>
                              {hours.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                            <select
                              id={`sld-end-time-minute-${index}-recurring`}
                              className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              onChange={(e) => {
                                const existingValues = [
                                  ...selectedPoi.availability_detail.SLD,
                                ];
                                existingValues[index].end_time.minute =
                                  e.target.value;
                                setSelectedPoi({
                                  ...selectedPoi,
                                  availability_detail: {
                                    ...selectedPoi.availability_detail,
                                    SLD: existingValues,
                                  },
                                });
                              }}
                              defaultValue={parseInt(
                                selectedPoi.availability_detail.SLD[index]
                                  .end_time.minute,
                                10,
                              )}
                            >
                              <option value="">
                                {translateText("minute", language)}
                              </option>
                              {minutes.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt < 10 ? `0${opt}` : opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* Status */}
              <Dropdown
                id="status"
                className="col-span-full"
                label={translateText("status", language)}
                showLabel={true}
                required={true}
                state={selectedPoi}
                stateName="status"
                onChange={setSelectedPoi}
                defaultValue={selectedPoi.status[0]}
              >
                {poiStatusChoices.map((opt) => (
                  <option key={opt[0]} value={opt[0]}>
                    {opt[1]}
                  </option>
                ))}
              </Dropdown>

              {/* Danger zone */}
              <DangerZone
                objectId={selectedPoi.id}
                handler={confirmDeletePoi}
                language={language}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {pointsOfInterest.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            <Filters
              language={language}
              className="pb-10"
              filters={[
                {
                  id: "category",
                  name: "Category",
                  options: poiCategory,
                },
                {
                  id: "type",
                  name: "Type",
                  options: poiTypes,
                },
              ]}
              queryParams={[
                ["category", searchCategory],
                ["type", searchType],
              ]}
              handlers={{
                category: {
                  state: category,
                  stateName: "value",
                  onChange: setCategory,
                },
                type: {
                  state: type,
                  stateName: "value",
                  onChange: setType,
                },
              }}
              loading={pointsOfInterest.loading}
              baseUrl="/orientation/points-of-interest"
              showDateRange={false}
              showFilters={true}
              showSearchBar={false}
            />
            {poisData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {poisData.map((poi: PointOfInterestState) => (
                    <a
                      href="#!"
                      key={poi.id}
                      onClick={(e) => selectPoiToEdit(e, [poi.id])}
                      className="relative"
                    >
                      {/* Point of interest data */}
                      <Card
                        key={poi.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {poi.name[language]}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          <div className="inline-flex text-sm text-secondary">
                            <MapPinIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            {poi.category[1]}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <TagIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            <span className="truncate">{poi.type[1]}</span>
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <ClockIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {poi.datetime_created}
                          </div>
                        </div>
                      </Card>

                      {/* Status indicator */}
                      <Indicator
                        size={3}
                        colour={
                          poi.status[0] === "O"
                            ? "success"
                            : poi.status[0] === "C"
                            ? "danger"
                            : "warning"
                        }
                        pulse={false}
                      />
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_pois", language)}
                subtext={translateText("no_pois_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
